import axios from 'axios';

/*
  Signup -> plan -> Stripe. The plan picked on the signup screen is kept in
  localStorage until the account is able to pay: right after registration,
  or once the emailed confirmation link signs the person in. BillingGate then
  sends them straight to Stripe Checkout instead of into the workspace.
*/

const PENDING_KEY = 'xp_pending_checkout';
const PENDING_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
export const PLAN_KEYS = ['start', 'pro', 'team', 'network', 'enterprise'];
export const INTERVAL_KEYS = ['monthly', 'semiannual', 'annual'];

export function savePendingPlan(plan, interval) {
  if (!PLAN_KEYS.includes(plan) || !INTERVAL_KEYS.includes(interval)) return;
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ plan, interval, at: Date.now() }));
  } catch (_e) {
    /* storage disabled: the Billing page still lets them pick again */
  }
}

export function readPendingPlan() {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || !PLAN_KEYS.includes(v.plan) || !INTERVAL_KEYS.includes(v.interval)) return null;
    if (typeof v.at !== 'number' || Date.now() - v.at > PENDING_MAX_AGE_MS) return null;
    return { plan: v.plan, interval: v.interval };
  } catch (_e) {
    return null;
  }
}

// Links from the website carry the chosen plan: /auth?plan=pro&interval=annual.
// ?signup=1 opens the sign-up tab without a plan.
function urlParams() {
  try {
    return new URLSearchParams(window.location.search || '');
  } catch (_e) {
    return new URLSearchParams();
  }
}

export function planFromUrl() {
  const params = urlParams();
  const plan = (params.get('plan') || '').toLowerCase();
  if (!PLAN_KEYS.includes(plan)) return null;
  let interval = (params.get('interval') || 'monthly').toLowerCase();
  if (!INTERVAL_KEYS.includes(interval)) interval = 'monthly';
  // Enterprise is sold monthly or for 12 months, never for 6.
  if (plan === 'enterprise' && interval === 'semiannual') interval = 'annual';
  return { plan, interval };
}

export function adoptPlanFromUrl() {
  const choice = planFromUrl();
  if (choice) savePendingPlan(choice.plan, choice.interval);
  return choice;
}

export function signupRequestedInUrl() {
  return Boolean(planFromUrl()) || urlParams().get('signup') === '1';
}

export function clearPendingPlan() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch (_e) {
    /* ignore */
  }
}

export async function startCheckout(api, plan, interval) {
  const origin = window.location.origin;
  const r = await axios.post(`${api}/billing/checkout`, null, {
    params: {
      plan,
      interval,
      success_url: `${origin}/billing?checkout=success`,
      cancel_url: `${origin}/billing?checkout=canceled`,
    },
  });
  const url = r.data && r.data.url;
  if (!url) throw new Error('Checkout did not return a payment page');
  window.location.assign(url);
  return url;
}

const STATUS_TTL_MS = 60 * 1000;
let statusCache = null;

export function peekBillingStatus() {
  return statusCache && Date.now() - statusCache.at < STATUS_TTL_MS ? statusCache.data : null;
}

export async function fetchBillingStatus(api, { fresh = false } = {}) {
  if (!fresh) {
    const cached = peekBillingStatus();
    if (cached) return cached;
  }
  try {
    const r = await axios.get(`${api}/billing/status`);
    statusCache = { at: Date.now(), data: r.data || {} };
  } catch (_e) {
    // Fail open: the server still refuses paid actions on its own (402).
    statusCache = { at: Date.now(), data: { enabled: false, unavailable: true } };
  }
  return statusCache.data;
}

export function invalidateBillingStatus() {
  statusCache = null;
}

export function isBlocked(status) {
  return Boolean(status && status.enabled && status.state && status.state.blocked);
}

export function hasLiveSubscription(status) {
  const sub = (status && status.subscription) || {};
  return Boolean(sub.stripe_subscription_id) && !isBlocked(status);
}
